import { asObject, formatCell, humanizeKey, toolErrorKey } from './agent-tool-result-format';
import type {
  GenericField,
  GenericNode,
  GenericObject,
  GenericTable,
  SiteResult,
  TableColumn,
  ToolResultGeneric,
  ToolResultView,
} from './agent-tool-result.model';
import { CURATED_BUILDERS } from './agent-tool-result-views';

// Builds a typed view of a tool result keyed by tool name (read-side sibling of agent-tool-mutations).
// No builder, or unparseable content, yields undefined and the caller falls back to raw JSON. Labels
// and notes are i18n keys resolved in the template.

export function buildToolResultView(toolName: string, content: unknown): ToolResultView | undefined {
  const perSite = siteEntries(content);
  if (perSite) {
    return {
      kind: 'sites',
      sites: perSite.map(({ siteId, data, error }): SiteResult =>
        error === undefined
          ? { siteId, view: buildToolResultView(toolName, data) ?? buildGenericView(data) }
          : { siteId, errorKey: toolErrorKey(error) },
      ),
    };
  }
  return CURATED_BUILDERS[toolName]?.(content);
}

const SITE_ENTRY_KEYS: ReadonlySet<string> = new Set(['siteId', 'data', 'error']);

// Agents' SiteFanOut shape: [{ siteId, data }] per site answered, [{ siteId, error }] per site that failed.
function siteEntries(content: unknown): { siteId: number; data?: unknown; error?: unknown }[] | undefined {
  if (!Array.isArray(content) || content.length === 0) {
    return undefined;
  }
  const entries = content.map(asObject);
  const isFanOut = entries.every(
    (entry) =>
      entry !== undefined &&
      typeof entry['siteId'] === 'number' &&
      ('data' in entry || 'error' in entry) &&
      Object.keys(entry).every((key) => SITE_ENTRY_KEYS.has(key)),
  );
  return isFanOut ? (entries as { siteId: number; data?: unknown; error?: unknown }[]) : undefined;
}

// Depth/size caps keep a large or deeply nested result from unfolding into a wall of content; the raw
// JSON toggle in the tool pill remains the escape hatch for the full payload.
const MAX_GENERIC_DEPTH = 3;
const MAX_GENERIC_LIST_ITEMS = 50;
const MAX_GENERIC_TABLE_COLUMNS = 12;
const MAX_GENERIC_OBJECT_FIELDS = 60;

// Structural fallback for any tool without a curated builder: renders parsed content as a key/value
// list, an inferred table, or a nested mix of the two. Returns undefined for a bare scalar or an empty
// payload (already readable as raw text), so the caller falls back to the raw string in that case.
export function buildGenericView(content: unknown): ToolResultGeneric | undefined {
  const node = toGenericNode(content, 0);
  if (node === undefined || node.type === 'scalar') {
    return undefined;
  }
  return { kind: 'generic', node };
}

function toGenericNode(value: unknown, depth: number, key?: string): GenericNode | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (isScalar(value)) {
    return { type: 'scalar', value: formatCell(value, key) };
  }
  // Past the cap, stop unfolding and show the remaining subtree as compact JSON rather than nesting on.
  if (depth >= MAX_GENERIC_DEPTH) {
    return { type: 'scalar', value: compactJson(value) };
  }
  if (Array.isArray(value)) {
    return genericListOrTable(value, depth, key);
  }
  if (typeof value === 'object') {
    return genericObject(value as Record<string, unknown>, depth);
  }
  return undefined;
}

function genericObject(obj: Record<string, unknown>, depth: number): GenericObject | undefined {
  const fields: GenericField[] = [];
  for (const [key, raw] of Object.entries(obj)) {
    const node = toGenericNode(raw, depth + 1, key);
    // Skip null/undefined and empty containers so optional-but-absent fields don't clutter the view.
    if (node === undefined) {
      continue;
    }
    fields.push({ key, label: humanizeKey(key), node });
  }
  if (fields.length === 0) {
    return undefined;
  }
  const hiddenFields = Math.max(0, fields.length - MAX_GENERIC_OBJECT_FIELDS);
  return { type: 'object', fields: fields.slice(0, MAX_GENERIC_OBJECT_FIELDS), hiddenFields };
}

// A list's items inherit its key, so the entries of "targetSiteIds" format as ids.
function genericListOrTable(items: unknown[], depth: number, key?: string): GenericNode | undefined {
  if (items.length === 0) {
    return undefined;
  }
  const shown = items.slice(0, MAX_GENERIC_LIST_ITEMS);
  // A uniform array of flat objects reads best as a table with columns inferred from the union of keys.
  if (shown.every(isFlatObject)) {
    return genericTable(shown as Record<string, unknown>[], items.length);
  }
  const nodes: GenericNode[] = [];
  for (const item of shown) {
    const node = toGenericNode(item, depth + 1, key);
    if (node !== undefined) {
      nodes.push(node);
    }
  }
  if (nodes.length === 0) {
    return undefined;
  }
  return { type: 'list', items: nodes, total: items.length, truncated: items.length > shown.length };
}

function genericTable(rows: Record<string, unknown>[], total: number): GenericTable | undefined {
  const allKeys: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!allKeys.includes(key)) {
        allKeys.push(key);
      }
    }
  }
  if (allKeys.length === 0) {
    return undefined;
  }
  const keys = allKeys.slice(0, MAX_GENERIC_TABLE_COLUMNS);
  const columns: TableColumn[] = keys.map((key) => ({ key, label: humanizeKey(key) }));
  const formattedRows = rows.map((row) => {
    const formatted: Record<string, string> = {};
    for (const key of keys) {
      formatted[key] = formatCell(row[key], key);
    }
    return formatted;
  });
  return {
    type: 'table',
    columns,
    rows: formattedRows,
    total,
    truncated: total > rows.length,
    hiddenColumns: allKeys.length - keys.length,
  };
}

function isScalar(value: unknown): boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function isFlatObject(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Object.values(value as Record<string, unknown>).every(
    (entry) => entry === null || entry === undefined || isScalar(entry),
  );
}

function compactJson(value: unknown): string {
  const text = JSON.stringify(value);
  if (text === undefined) {
    return '';
  }
  const MAX_LENGTH = 200;
  return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH)}...` : text;
}
